import { useLingui } from "@lingui/react/macro";
import { SearchIcon } from "lucide-react";
import { useId, useState } from "react";

import { cn } from "@/shared/lib";

export type HeroChoice = { id: number; name: string; iconUrl: string | null };

type HeroPickerProps = {
  heroes: readonly HeroChoice[];
  selected: number | undefined;
  onSelect: (heroId: number) => void;
};

// Выбор героя: поиск по имени и сетка портретов, выбранный — с золотой рамкой. На узком
// блоке (телефон) портреты — лентой с прокруткой вбок, а не девятью рядами.
export const HeroPicker = ({ heroes, selected, onSelect }: HeroPickerProps) => {
  const { t } = useLingui();
  const inputId = useId();
  const [query, setQuery] = useState("");
  const needle = query.trim().toLowerCase();
  const shown =
    needle === "" ? heroes : heroes.filter((h) => h.name.toLowerCase().includes(needle));
  return (
    <section
      aria-label={t`Hero choice`}
      className="@container flex flex-col gap-3 rounded-xl border border-border bg-surface px-4 py-3.5"
    >
      <label
        htmlFor={inputId}
        className="flex min-h-11 max-w-80 items-center gap-2.5 rounded-lg border border-border bg-background px-3 focus-within:border-accent"
      >
        <SearchIcon aria-hidden="true" className="size-4.5 text-subtle" />
        <span className="sr-only">{t`Find a hero`}</span>
        <input
          id={inputId}
          type="search"
          value={query}
          placeholder={t`Find a hero`}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
          className="min-w-0 flex-1 bg-transparent text-[15px] text-foreground outline-none placeholder:text-subtle"
        />
      </label>
      {/* Узкий блок (телефон) — лента портретов с прокруткой вбок, как на доске. */}
      <ul
        aria-label={t`Heroes`}
        className="-mx-1 flex [scrollbar-width:thin] gap-2 overflow-x-auto px-1 py-1 @lg:mx-0 @lg:flex-wrap @lg:overflow-visible @lg:px-0"
      >
        {shown.map((hero) => {
          const isSelected = hero.id === selected;
          return (
            <li key={hero.id} className="shrink-0">
              <button
                type="button"
                aria-pressed={isSelected}
                aria-label={hero.name}
                title={hero.name}
                onClick={() => {
                  onSelect(hero.id);
                }}
                className={cn(
                  "block size-13 overflow-hidden rounded-[10px] bg-surface-raised p-0 transition-opacity",
                  isSelected
                    ? "border-2 border-accent shadow-[0_0_0_2px_var(--color-background),0_0_0_4px_color-mix(in_srgb,var(--color-accent)_35%,transparent)]"
                    : "border border-border opacity-80 hover:opacity-100",
                )}
              >
                {hero.iconUrl === null ? (
                  <span aria-hidden="true" className="font-display text-muted">
                    {hero.name.slice(0, 1)}
                  </span>
                ) : (
                  <img
                    src={hero.iconUrl}
                    alt=""
                    width={52}
                    height={52}
                    className="size-full object-cover"
                  />
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
};
